// Minimal, dependency-free decode of a JWT payload. We NEVER trust these claims
// for authorization (the server does that on every call) — they only drive the
// UI: which nav/dashboard/actions to show. Signature is not verified client-side.

export interface AccessClaims {
  userId?: number;
  tenantId?: string;
  role?: string;
  email?: string;
  locale?: string;
  exp?: number;
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, "+").replace(/_/g, "/");
  const withPad = padded + "=".repeat((4 - (padded.length % 4)) % 4);
  if (typeof atob === "function") return atob(withPad);
  // Node/SSR fallback (tests).
  return Buffer.from(withPad, "base64").toString("binary");
}

/** Decode the access-token payload. Returns {} on any malformed input. */
export function decodeAccessToken(token: string | null | undefined): AccessClaims {
  if (!token) return {};
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) return {};
  try {
    const json = JSON.parse(
      decodeURIComponent(
        base64UrlDecode(parts[1])
          .split("")
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(""),
      ),
    ) as Record<string, unknown>;
    const sub = json.sub;
    const claims: AccessClaims = {};
    if (typeof sub === "string") claims.userId = Number(sub);
    else if (typeof sub === "number") claims.userId = sub;
    if (typeof json.tenantId === "string") claims.tenantId = json.tenantId;
    if (typeof json.role === "string") claims.role = json.role;
    if (typeof json.email === "string") claims.email = json.email;
    if (typeof json.locale === "string") claims.locale = json.locale;
    if (typeof json.exp === "number") claims.exp = json.exp;
    return claims;
  } catch {
    return {};
  }
}
